// @flow
import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SockJsClient from 'react-stomp';
import { Config } from '../../../latest/src/constants/AppServerConfig';
import {
  setAttendees,
  setClientLeftWaitingRoom,
  setWaitingRoomMembers,
  setJoinData,
  setClientJoinedIn,
  setChimeSessionData,
} from '../../../redux/actions/teleTherapyActions';
import { setFlashNotification } from '../../../redux/modules/flashNotification';
import { useNavigate, useLocation } from 'react-router-dom';
import Storage from '../../../latest/src/utils/Storage';
import {
  clearModalContent,
  setModalContent,
} from '../../../redux/modules/modalEvent';
import { addClientResponse } from '../../../redux/modules/questionnaireResponse';
import {
  endTherapySession,
  therapistAcknowledgeSession,
  therapistStartSessionNewEndPoint,
} from '../../../api/therapySession';
import { getClientSubmittedResponse } from '../../../redux/services/questionnaireServices';
import { Logger } from 'src/latest/src/services';
import { StorageKey } from 'src/latest/src/constants/StorageKey';

type Props = {
  loggedInUser: Object,
};

export default function TeletherapySocket({
  loggedInUser,
}: Props): React$Element<any> {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const isTherapist = loggedInUser?.user_type?.includes('THERAPIST');

  const headerKeys = {
    APIKEY: 'test',
    SECRET: 'test',
    CLIENTID: loggedInUser.id,
  };
  const subscribeHeaders = {
    APIKEY: 'test',
    SECRET: 'test',
    CLIENTID: loggedInUser.id,
  };

  const socketRef: any = useRef();
  const history = useNavigate();

  const { attendees } = useSelector(
    ({ teleTherapy, questionnaireResponse }) => ({
      ...teleTherapy,
      ...questionnaireResponse,
    }),
  );

  const handleStartSession = async client => {
    client = client[0];
    const id = client.id;
    const fullName = client.display_name;

    const newAttendees = [
      {
        id,
        fullName,
        clientId: client.clients[0].client_id,
        isTherapist: false,
        image: 'https://via.placeholder.com/150',
        checkinQuestionnaireId: client.clients[0].checkin_questionnaire_id,
        clusterId: client.cluster_id,
        therapySessionId: id,
      },
      {
        id: loggedInUser.id,
        fullName: loggedInUser.full_name,
        isTherapist: true,
        sessionId: id,
        image: 'https://via.placeholder.com/150',
      },
    ];

    dispatch(setAttendees({ attendees: newAttendees }));

    Storage.setItem('isStartedTherapist', true);

    await therapistAcknowledgeSession(client.id);
    const therapistChimeData = await therapistStartSessionNewEndPoint(
      newAttendees[0].id,
    ); // start the session by therapist

    await dispatch(
      setJoinData({ joinData: therapistChimeData, isReadyToJoin: false }),
    );

    navigate('/teletherapy/waiting-room');
  };

  const handleReceiveMessage = async message => {
    try {
      if (message.socket_message_type === 'CLIENT_INITIATED_SESSION') {
        // play the audio when client joins
        const audioPath = '/audio/PianoChimeFinal.mp3';
        const audio = new Audio(audioPath);

        // only play sound when therapists is not in meeting
        if (
          !location.pathname.includes('/teletherapy/meeting-room') &&
          !location.pathname.includes('/teletherapy/waiting-room')
        ) {
          audio.play();
        }

        dispatch(
          setWaitingRoomMembers({
            waitingRoomMembers: restructureIncomingSessionData(message),
          }),
        );
        dispatch(setClientJoinedIn());

        // default notification value
        const notification = {
          message: message?.display_name + ' has joined the session',
          hasButton: true,
        };

        // do not show meeting start  button on notification if therapist on meeting room or waiting room or in intake questions
        if (
          location.pathname.includes('/teletherapy/meeting-room') ||
          location.pathname.includes('/teletherapy/waiting-room') ||
          location.pathname === '/therapy/initquestions'
        ) {
          notification.message =
            message?.display_name + ' has entered your waiting room';
          notification.hasButton = false;
        }

        dispatch(
          setFlashNotification({
            message: notification.message,
            isError: true,
            duration: 5000,
            hasButton: notification.hasButton,
            userId: message?.client_id,
            userName: message?.client_name,
            photoUrl: message?.client_profile_picture, // add avatar to notification
            buttonAction: () => {
              handleStartSession(restructureIncomingSessionData(message));
            },
            buttonLabel: 'Start Session',
          }),
        );

        if (isTherapist) {
          await handleFetchClientResponse(message);
        }
      } else if (message.socket_message_type === 'CLIENT_JOINED_SESSION') {
        const notification = {
          message: message?.display_name + ' has joined the session',
          hasButton: false,
        };

        if (
          location.pathname.includes('/teletherapy/meeting-room') ||
          location.pathname.includes('/teletherapy/waiting-room')
        ) {
          dispatch(
            setFlashNotification({
              message: notification.message,
              isError: true,
              hasButton: notification.hasButton,
              userId: message?.client_id,
              userName: message?.client_name,
              photoUrl: message?.client_profile_picture, // add avatar to notification
              duration: 5000,
            }),
          );
        }
      } else if (['CHIME_JOIN_DATA'].includes(message.socket_message_type)) {
        startChimeSession(message.chime_data);
        dispatch(setChimeSessionData(message.chime_data));
      } else if (message.socket_message_type === 'SESSION_ENDED_BY_THERAPIST') {
        dispatch(
          setFlashNotification({
            message:
              message?.therapist_name?.substr(0, 25) + ' has ended the session',
            isError: true,
            userId: message?.client_id,
            userName: message?.therapist_name,
            photoUrl: message?.therapist_profile_picture, // add avatar to notification
            duration: 5000,
          }),
        );
        dispatch(clearModalContent());
        history('/');
      } else if (message.socket_message_type === 'CLIENT_LEFT_THE_SESSION') {
        dispatch(
          setClientLeftWaitingRoom({
            leftWaitingRoomMembers: [message],
          }),
        );

        dispatch(
          setFlashNotification({
            message:
              message?.display_name?.substr(0, 25) + ' has left the session',
            isError: true,
            userId: message?.client_id,
            userName: message?.therapist_name,
            photoUrl: message?.client_profile_picture, // add avatar to notification
            duration: 5000,
          }),
        );

        if (location.pathname.includes('room')) {
          if (Storage.getItem('sessionType') === 'INDIVIDUAL') {
            await endTherapySession();
            history({
              pathname: '/therapy/initquestions',
              search: '?fetchQuestionnaire=true',
            });
          }
        } else {
          if (Storage.getItem('sessionType') === 'INDIVIDUAL') {
            await endTherapySession();
          }
        }
      }
    } catch (err) {
      process.env.REACT_APP_ENVIRONMENT === 'dev' ||
      process.env.REACT_APP_ENVIRONMENT === 'qa'
        ? alert(err)
        : Logger.log(err);
      dispatch(
        setFlashNotification({
          message: "There's and error connecting to websocket.",
          isError: true,
        }),
      );
    }
  };

  const handleFetchClientResponse = async ({ clientData }) => {
    if (!clientData) return;
    const { data, clientName, questionnaireDto, isCompleted, sessionType } =
      await getClientSubmittedResponse(clientData);
    const questionnaireCompletionId = clientData.checkinQuestionnaireId;

    if (isCompleted) {
      dispatch(
        addClientResponse({
          clientId: clientData.clientId,
          clientName,
          questionnaireCompletionId,
          question: questionnaireDto,
          response: data,
          isCompleted,
          sessionType,
        }),
      );
    }
  };

  const startChimeSession = async joinInfo => {
    dispatch(setJoinData({ joinData: joinInfo, isReadyToJoin: true }));
    dispatch(setChimeSessionData({ joinData: joinInfo, isReadyToJoin: false }));
    const storedAskAutoStart = JSON.parse(
      Storage.getItem(StorageKey.shouldAlwaysAsk),
    );

    if (loggedInUser.userRole === 'client') {
      dispatch(
        setFlashNotification({
          message:
            joinInfo?.therapist_name?.substr(0, 25) +
            ` has started the session`,
          isError: true,
          userName: joinInfo?.therapist_name,
          photoUrl: joinInfo?.therapist_profile_picture, // add avatar to notification
          duration: 5000,
        }),
      );
    }

    if (
      !storedAskAutoStart &&
      storedAskAutoStart !== null &&
      attendees.length > 0
    ) {
      dispatch(setModalContent({ isLoadingPopup: false }));
      dispatch(clearModalContent());
      history('/teletherapy/meeting-room');
    }
  };

  const handleConnectionSuccess = async () => {
    try {
      Logger.log('websocket: connected successfuly');
      // if (isTherapist && location.pathname.includes('intake')) {
      //     const { waitingRoomMembers } = await getWaitingRoomMembers();

      //     dispatch(setWaitingRoomMembers({ waitingRoomMembers }));
      // }
    } catch (err) {
      process.env.REACT_APP_ENVIRONMENT === 'dev' ||
      process.env.REACT_APP_ENVIRONMENT === 'qa'
        ? alert(err)
        : Logger.log(err);

      dispatch(
        setFlashNotification({
          message: "There's and error connecting to websocket.",
          isError: true,
          duration: 5000,
        }),
      );
    }
  };

  const topicSubscriptions = [
    '/topic/waiting-room',
    '/user/topic/waiting-room',
  ];

  const onConnectFailure = () => {
    if (
      location.pathname.includes('/therapy/therapist') ||
      location.pathname.includes('/therapy/client')
    ) {
      // setFailure(true);
      // setTimeout(() => {
      //     setFailure(false);
      // }, 1000);
    }
  };

  const onDisconnect = () => {
    if (
      location.pathname.includes('/therapy/therapist') ||
      location.pathname.includes('/therapy/client')
    ) {
      Logger.log('websocket: disconnect');
    }
  };

  const restructureIncomingSessionData = sessionData => {
    return [
      {
        id: sessionData.therapy_session_id,
        cluster_id: sessionData.cluster_id,
        display_name: sessionData.client_name,
        therapist_name: sessionData.therapist_name,
        therapist_pronoun: sessionData.therapist_pronoun,
        clients: [
          {
            client_id: sessionData.client_id,
            therapy_session_member_state: sessionData.socket_message_type,
            checkin_questionnaire_id: sessionData.checkin_questionnaire_id,
            client_name: sessionData.client_name,
            client_pronoun: sessionData.client_pronoun,
          },
        ],
        photoUrl: sessionData.client_profile_picture,
      },
    ];
  };

  return (
    <SockJsClient
      ref={socketRef}
      url={`${Config.apiUrl}/${Config.therapySessionService}socket`}
      headers={headerKeys}
      subscribeHeaders={subscribeHeaders}
      autoReconnect={true}
      topics={topicSubscriptions}
      onMessage={handleReceiveMessage}
      onConnect={handleConnectionSuccess}
      onConnectFailure={onConnectFailure}
      onDisconnect={onDisconnect}
      heartbeat={10}
      options={{ timeout: 10000 }}
    />
  );
}
